import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import OVERVIEW1 from '../../../../app/assets/images/projectImage/quivverImages/overview1.webp'
import OVERVIEW2 from '../../../../app/assets/images/projectImage/quivverImages/overview2.webp'
import OVERVIEW3 from '../../../../app/assets/images/projectImage/quivverImages/overview3.webp'

const QuivverProjectOverview = () => {
    const containerRef = useRef(null)
    const isInView = useInView(containerRef, { once: false, amount: 0.2 })

    // Title animation variant - from top
    const titleVariant = {
        hidden: { opacity: 0, y: -60, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.1, 0.25, 1.0]
            }
        }
    }

    // Purple panel animation variant
    const panelVariant = {
        hidden: { opacity: 0, scaleX: 0.8, y: 20 },
        visible: {
            opacity: 1,
            scaleX: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.1, 0.25, 1.0],
                delay: 0.2
            }
        }
    }

    // Container for images with stagger
    const imagesContainerVariant = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.4
            }
        }
    }

    // Individual image animation variant - alternating from left, right, and top
    const imageVariant = (index: number) => ({
        hidden: {
            opacity: 0,
            x: index === 0 ? -80 : index === 1 ? 0 : 80,
            y: index === 1 ? -50 : 50,
            scale: 0.8,
            rotateY: index === 0 ? -15 : index === 2 ? 15 : 0
        },
        visible: {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            rotateY: 0,
            transition: {
                duration: 0.7,
                ease: [0.25, 0.1, 0.25, 1.0],
                type: "spring",
                stiffness: 100,
                damping: 15
            }
        },
        hover: {
            scale: 1.05,
            y: -10,
            rotateY: 5,
            transition: {
                duration: 0.4,
                ease: "easeInOut"
            }
        }
    })

    // Text animation variant
    const textVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.1, 0.25, 1.0],
                delay: 0.6
            }
        }
    }

    return (
        <section className="bg-[#0F0D16] text-white py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24">
            <div className="" ref={containerRef}>

                {/* Title + Purple Background + Images */}
                <div className="flex flex-col items-center relative">
                    {/* Title */}
                    <motion.h2
                        className="uppercase font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl text-[#51C5FF] mb-6 sm:mb-8 md:mb-12 lg:mb-16 xl:mb-20 text-center"
                        variants={titleVariant}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                    >
                        Overview
                    </motion.h2>

                    {/* Container for images and purple panel */}
                    <div className="relative w-full mt-6 sm:mt-8 md:mt-12 lg:mt-16 xl:mt-20">
                        {/* Purple panel */}
                        <motion.div
                            className="relative w-full h-[8rem] xs:h-[10rem] sm:h-[12rem] md:h-[16rem] lg:h-[18rem] xl:h-[20rem] 2xl:h-[22rem] bg-[#2DEE9A]"
                            variants={panelVariant}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                        >
                        </motion.div>

                        {/* Floating images - positioned to overlap purple band (bottom half inside, top half above) */}
                        <motion.div
                            className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex flex-row items-center justify-center gap-2 xs:gap-3 sm:gap-4 md:gap-5 lg:gap-6 px-2 xs:px-3 sm:px-4 md:px-6 z-10"
                            variants={imagesContainerVariant}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                        >
                            <motion.img
                                src={OVERVIEW1}
                                className="w-[30%] xs:w-[40%] sm:w-[30%] md:w-[26%] lg:w-[24%] xl:w-[22%] 2xl:w-[20%] max-w-[344px] drop-shadow-2xl object-contain"
                                alt="Overview 1"
                                variants={imageVariant(0)}
                                whileHover="hover"
                            />
                            <motion.img
                                src={OVERVIEW2}
                                className="w-[30%] xs:w-[40%] sm:w-[30%] md:w-[26%] lg:w-[24%] xl:w-[22%] 2xl:w-[20%] max-w-[344px] drop-shadow-2xl object-contain"
                                alt="Overview 2"
                                variants={imageVariant(1)}
                                whileHover="hover"
                            />
                            <motion.img
                                src={OVERVIEW3}
                                className="w-[30%] xs:w-[40%] sm:w-[30%] md:w-[26%] lg:w-[24%] xl:w-[22%] 2xl:w-[20%] max-w-[344px] drop-shadow-2xl object-contain"
                                alt="Overview 3"
                                variants={imageVariant(2)}
                                whileHover="hover"
                            />
                        </motion.div>
                    </div>
                </div>

                {/* Text BELOW the background */}
                <motion.div
                    className="mt-8 sm:mt-12 md:mt-16 lg:mt-20 xl:mt-24 2xl:mt-32 max-w-7xl px-2 sm:px-4 md:px-6 lg:px-10 xl:px-14 2xl:px-16 mx-auto text-center text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl leading-relaxed sm:leading-relaxed md:leading-relaxed text-gray-300"
                    variants={textVariant}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    This is a competitive gaming platform that connects players through
                    live tournaments, matchmaking, and real-time interaction. Designed
                    for modern gamers, the app brings multiple games, player profiles,
                    chats, and tournament participation into one powerful, visually
                    immersive mobile experience. From casual players to competitive
                    esports enthusiasts, it makes discovering, joining, and managing
                    tournaments seamless and exciting.
                </motion.div>

            </div>
        </section>
    )
}

export default QuivverProjectOverview
